import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../Loader";
import Pagination from "../Pagination";
import AuthContext from "../../contexts/AuthProvider";
import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const SubRequestsTable = ({ onCountChange, statusFilter, skipInitialCountUpdate = false }) => {
  const { i18n,t } = useTranslation("booking");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subRequests, setSubRequests] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const { isAuthenticated } = useContext(AuthContext);

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const fetchSubRequests = async (page = 1) => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return { data: [], pagination: { currentPage: 1, totalPages: 1, totalItems: 0 } };
      }

      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/subOrders?page=${page}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        navigate("/login");
        throw new Error("Unauthorized access. Please log in again.");
      }
      if (!response.ok) {
        throw new Error("Failed to fetch sub requests.");
      }

      const json = await response.json();
      return {
        data: json.data,
        pagination: {
          currentPage: json.currentPage,
          totalPages: json.totalPages,
          totalItems: json.totalItems
        }
      };
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
      return { data: [], pagination: { currentPage: 1, totalPages: 1, totalItems: 0 } };
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const result = await fetchSubRequests(1);
      setSubRequests(result.data);
      setPagination(result.pagination);
      
      // Notify parent component about the count only if not skipping initial update
      if (onCountChange && !skipInitialCountUpdate) {
        onCountChange(result.pagination.totalItems);
      }
      
      setLoading(false);
    })();
  }, [onCountChange]);

  const handlePageChange = async (newPage) => {
    if (newPage === pagination.currentPage) return;
    
    setLoading(true);
    const result = await fetchSubRequests(newPage);
    setSubRequests(result.data);
    setPagination(result.pagination);
    setLoading(false);
  };

  const transformedSubRequests = subRequests.map((subRequest) => ({
    id: subRequest.id,
    order_id: subRequest.order_id,
    customer_name: subRequest.customer_name || subRequest.user_name || "Unknown",
    venue_name: subRequest.venue_name || "Unknown",
    num_attendees: subRequest.attendees || subRequest.attendees|| 0,
    submitted_on: subRequest.submitted_on || subRequest.created_at
      ? new Date(subRequest.submitted_on || subRequest.created_at)
          .toLocaleString(
            i18n.language === "ar" ? "ar-EG" : "en-GB",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          .replace(",", " -")
      : "Unknown",
    status: subRequest.status || "Unknown",
  }));

  const filteredSubRequests = transformedSubRequests.filter((subRequest) =>
    statusFilter === "allStatus" || subRequest.status === statusFilter
  );

  const handleSubRequestDetails = (id) => {
    navigate(`/booking-details/${id}`);
  };

  if (loading) {
    return (
      <div className="booking-loader-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="sub-requests-table-container">

      {/* Sub Requests Table */}
      <div className="booking-table table-wrapper">
        <table className="bookings-table">
          <thead>
            <tr>
              <th>{t("subRequests.tableHeaders.customer")}</th>
              <th>{t("subRequests.tableHeaders.venue")}</th>
              <th>{t("subRequests.tableHeaders.numAttendees")}</th>
              <th>{t("subRequests.tableHeaders.submittedOn")}</th>
              <th>{t("subRequests.tableHeaders.status")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubRequests.length > 0 ? (
              filteredSubRequests.map((subRequest) => {
                const key = subRequest.status.toLowerCase();
                return (
                  <tr
                    key={subRequest.id}
                    onClick={() => handleSubRequestDetails(subRequest.order_id)}
                  >
                    <td>{subRequest.customer_name}</td>
                    <td>{subRequest.venue_name}</td>
                    <td>{subRequest.num_attendees}</td>
                    <td>{subRequest.submitted_on}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          (key === "accepted" || key === "paid") && "confirmed-booking"
                        } ${
                          (key === "rejected" || key === "cancelled") && "rejected"
                        } ${
                          key === "pending" && "pending-status"
                        }`}
                      >
                        {t(`badgeStatus.${key}`)}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className="no-data">
                  {t("noData")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="booking-mobile-table">
        {filteredSubRequests.length > 0 ? (
          <ul>
            {filteredSubRequests.map((subRequest) => {
              const key = subRequest.status.toLowerCase();
              return (
                <li
                  key={subRequest.id}
                  className="booking-card"
                  onClick={() => handleSubRequestDetails(subRequest.order_id)}
                >
                  <div className="booking-card-content">
                    <div className="booking-name">
                      {subRequest.customer_name}, {subRequest.venue_name}
                    </div>
                    <div className="booking-info">
                      {t("subRequests.attendees")}: {subRequest.num_attendees} | {subRequest.submitted_on}
                    </div>
                  </div>
                  <div className="booking-status">
                     <span
                        className={`status-badge ${
                          (key === "accepted" || key === "paid") && "confirmed-booking"
                        } ${
                          (key === "rejected" || key === "cancelled") && "rejected"
                        } ${
                          key === "pending" && "pending-status"
                        }`}
                      >
                        {t(`badgeStatus.${key}`)}
                      </span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div>
            <div className="no-data">{t("noData")}</div>
          </div>
        )}
      </div>

      {/* Pagination Component */}
      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          handlePageChange={handlePageChange}
          maxVisiblePages={5}
          isRTL={getLangFromLocalStorage() === "ar"}
        />
      )}
    </div>
  );
};

export default SubRequestsTable;